import { persistStateTypes as PERSIST_STATE_TYPES } from 'a/reducers/persistStateReducer';
import { PRODUCT_LECTURA, PRODUCT_READING } from 'root/lib/constants';

import {
  GAME_ID,
  GAME_ID_ES,
  SAVED_DATA_VERSION,
  ACTIONS,
  PERCENTILE_BOUNDS_BY_PRODUCT,
  PERFORMANCE_TO_STARS_MAP,
} from '../data/constants';

const PUT_WRITE_RESULTS_REQUEST = 'PUT_WRITE_RESULTS_REQUEST';
const PUT_WRITE_RESULTS_RESPONSE = 'PUT_WRITE_RESULTS_RESPONSE';

const OTHER_ACTION_TYPES = [
  PUT_WRITE_RESULTS_REQUEST,
  PUT_WRITE_RESULTS_RESPONSE,
  PERSIST_STATE_TYPES.REQUESTRESPONSE_GAMESTATE_ALL,
];

const initState = {
  activityId: '', // MTG or RWR (reading); LSC, SD, RWR, or MTG (lectura)
  isMtGSkillScanSentences: false, // true for Grade 2 students: Mind the Gap is their only measure, served as sentences
  redirectToSkillScan: false, // should skill scan be shown to the user
  isRedirectHardcoded: false, // for testing purposes only
  lastTimePlayed: 0, // last time skill scan was fully taken by the user
  teacherResetInfo: {
    teacherReset: false, // is teacher reset skill scan state
    resetCount: 0, // count of teacher reset clicks
    passageID: '', // current passage 2-5 Grades content id
    formID: '', // current form KG-1 content id
  },
  numOfStars: 0,
  shouldDisplayAmpGlobalNav: false,
  areMeasuresCompleted: false,
  measuresIds: [],
  measures: [],
  measuresRemaining: [],
  measuresCompleted: [],
  aggregateScoreData: { // for lectura students with multiple measures
    level: 1, // needed for ROUND_COMPLETE event
    questionCount: 0,
    questionsCorrect: 0,
    questionsSkipped: 0,
    questionsSkippedOver: 0,
    questionsWrong: 0,
  },
  skillScanSessionId: null, // sent with /writeResult for growth results
  skillScanSessionData: {}, // for lectura - stores timestamps for aggregate result (start/complete)
};

const getNumOfStars = ({
  RoundId,
  LowResponse = false,
  Rushed = false,
  PerformanceInterpretation,
  PercentileRank,
} = {}, product) => {
  if (!product || !RoundId || Rushed || LowResponse) {
    return 0;
  }

  if (product === PRODUCT_READING && typeof PerformanceInterpretation === 'string') {
    return PERFORMANCE_TO_STARS_MAP[PerformanceInterpretation] || 0;
  }

  const numPercentile = +PercentileRank;
  const { lowerBound, upperBound } = PERCENTILE_BOUNDS_BY_PRODUCT[product];
  if (numPercentile < lowerBound) {
    return 1;
  }
  if (numPercentile > upperBound) {
    return 3;
  }
  return 2;
};

const skillScanReducer = (state = initState, { type, gameId, payload, stateData, product }) => {
  if (![GAME_ID, GAME_ID_ES].includes(gameId) && !OTHER_ACTION_TYPES.includes(type)) {
    return state;
  }

  switch (type) {
    case ACTIONS.SET_SKILL_SCAN_MEASURES: {
      const { ids, measures, isMtGSkillScanSentences } = payload;
      let {
        measuresRemaining, measuresCompleted, aggregateScoreData, skillScanSessionId, skillScanSessionData,
      } = state;

      // Nothing left to resume - whether a true fresh start or a prior cycle that finished without
      // clearing measuresCompleted - means start over with this cycle's ids.
      // Otherwise resume the in-progress values already in state.
      if (!measuresRemaining.length) {
        measuresRemaining = ids;
        measuresCompleted = [];
        // a restarted cycle must not inherit the abandoned cycle's score or session identity
        ({ aggregateScoreData, skillScanSessionId, skillScanSessionData } = initState);
      }
      const [activityId] = measuresRemaining;

      return {
        ...state,
        activityId,
        measuresIds: [...ids],
        measures,
        measuresRemaining,
        measuresCompleted,
        isMtGSkillScanSentences,
        aggregateScoreData,
        skillScanSessionId,
        skillScanSessionData,
      };
    }

    case ACTIONS.SET_SKILL_SCAN_ACTIVITY: {
      const [activityId] = payload;
      return {
        ...state,
        activityId,
      };
    }

    case ACTIONS.SET_SKILL_SCAN_REDIRECT_FLAG: {
      return {
        ...state,
        redirectToSkillScan: payload,
      };
    }

    case ACTIONS.SET_SKILL_SCAN_REDIRECT_HARDCODED: {
      let { measuresRemaining, measuresCompleted } = state;

      // If payload is true, this will allow dev skill scan reset
      // to mimic TD functionality without updating gamestate.
      if (payload === true) {
        measuresRemaining = [];
        measuresCompleted = [];
      }

      return {
        ...state,
        redirectToSkillScan: payload,
        isRedirectHardcoded: payload,
        teacherResetInfo: {
          ...state.teacherResetInfo,
          teacherReset: payload,
        },
        measuresRemaining,
        measuresCompleted,
        aggregateScoreData: payload ? initState.aggregateScoreData : state.aggregateScoreData,
        skillScanSessionId: payload ? null : state.skillScanSessionId,
        skillScanSessionData: payload ? {} : state.skillScanSessionData,
      };
    }

    case ACTIONS.SET_SKILL_SCAN_LAST_TIME_PLAYED: {
      return {
        ...state,
        lastTimePlayed: payload,
      };
    }

    case ACTIONS.SET_SKILL_SCAN_PASSAGE: {
      return {
        ...state,
        teacherResetInfo: {
          ...state.teacherResetInfo,
          passageID: payload,
        },
      };
    }

    case ACTIONS.SET_SKILL_SCAN_FORM: {
      return {
        ...state,
        teacherResetInfo: {
          ...state.teacherResetInfo,
          formID: payload,
        },
      };
    }

    case ACTIONS.SET_SKILL_SCAN_SD_FORM:
    case ACTIONS.SET_SKILL_SCAN_LSC_FORM: {
      return {
        ...state,
        teacherResetInfo: {
          ...state.teacherResetInfo,
          formID: payload,
        },
      };
    }

    case ACTIONS.SET_SKILL_SCAN_AGGREGATE_SCORE: {
      const { aggregateScoreData } = state;
      const { questionCount, questionsCorrect, questionsSkipped, questionsSkippedOver, questionsWrong } = payload;
      // Not all measures have same payload values so calculate to make putToWriteResultApi accurate
      const aggregateScore = (currentScore, newScore, altScore = 0) => currentScore + (newScore ?? altScore);

      return {
        ...state,
        aggregateScoreData: {
          ...aggregateScoreData,
          questionCount: aggregateScore(aggregateScoreData.questionCount, questionCount),
          questionsCorrect: aggregateScore(aggregateScoreData.questionsCorrect, questionsCorrect),
          questionsSkipped: aggregateScore(aggregateScoreData.questionsSkipped, questionsSkipped),
          questionsSkippedOver: aggregateScore(aggregateScoreData.questionsSkippedOver, questionsSkippedOver),
          questionsWrong: aggregateScore(
            aggregateScoreData.questionsWrong, questionsWrong, questionCount - questionsCorrect,
          ),
        },
      };
    }

    case ACTIONS.SET_SKILL_SCAN_SESSION_DATA: {
      const { skillScanSessionId, skillScanSessionData } = payload;
      return {
        ...state,
        skillScanSessionId,
        skillScanSessionData,
      };
    }

    case ACTIONS.SET_SKILL_SCAN_MEASURE_COMPLETE: {
      const { measuresRemaining, measuresCompleted } = payload;
      return {
        ...state,
        measuresRemaining,
        measuresCompleted,
      };
    }

    case ACTIONS.CLEAR_TEACHER_RESET_INFO: {
      return {
        ...state,
        teacherResetInfo: {
          ...initState.teacherResetInfo,
        },
      };
    }

    case PUT_WRITE_RESULTS_RESPONSE: {
      const { measures, measuresCompleted } = state;
      const areMeasuresCompleted = !!measures.length && measuresCompleted.length === measures.length;
      const numOfStars = payload ? getNumOfStars(payload.growthResult, payload.Product) : state.numOfStars;

      return {
        ...state,
        areMeasuresCompleted,
        numOfStars,
      };
    }

    case ACTIONS.SET_SHOULD_DISPLAY_AMP_GLOBAL_NAV: {
      return {
        ...state,
        shouldDisplayAmpGlobalNav: payload,
      };
    }

    case PERSIST_STATE_TYPES.REQUESTRESPONSE_GAMESTATE_ALL:
    case PERSIST_STATE_TYPES.REQUESTRESPONSE_GAMESTATE: {
      const isLecturaProduct = product === PRODUCT_LECTURA || gameId === GAME_ID_ES;
      const gameStateBucket = isLecturaProduct ? GAME_ID_ES : GAME_ID;
      const { GameData: savedData } = stateData[gameStateBucket] ?? {};
      const { isRedirectHardcoded } = state;

      const getDataFromSources = key => savedData?.gameDetails?.[key] || savedData?.[key] || state[key];
      const areMeasuresCompleted = getDataFromSources('areMeasuresCompleted');
      const lastTimePlayed = getDataFromSources('lastTimePlayed');
      const teacherResetInfo = getDataFromSources('teacherResetInfo');

      if (savedData && !isRedirectHardcoded) {
        if (areMeasuresCompleted || savedData.version !== SAVED_DATA_VERSION) {
          return {
            ...state,
            lastTimePlayed,
            teacherResetInfo,
          };
        }

        return {
          ...state,
          lastTimePlayed,
          teacherResetInfo,
          measuresRemaining: getDataFromSources('measuresRemaining'),
          measuresCompleted: getDataFromSources('measuresCompleted'),
          aggregateScoreData: getDataFromSources('aggregateScoreData'),
          skillScanSessionId: getDataFromSources('skillScanSessionId'),
          skillScanSessionData: getDataFromSources('skillScanSessionData'),
        };
      }

      return state;
    }

    default:
      return state;
  }
};

export default skillScanReducer;
